import { useMutation } from "@apollo/client";
import GET_COMPANION_SESSION_LIVEKIT_TOKEN from "@graphql/mutations/GetCompanionSessionLiveKitToken";
import {
  createLocalTracks,
  LocalAudioTrack,
  Room,
  RoomEvent,
} from "livekit-client";
import { createContext, useContext } from "react";
import { useRef, useState } from "react";

const CompanionContext = createContext();

export function CompanionProvider({ children }) {
  const [livekitRoom, setLivekitRoom] = useState(null);
  const [getCompanionLivekitToken] = useMutation(
    GET_COMPANION_SESSION_LIVEKIT_TOKEN,
  );
  const [companionSession, setCompanionSession] = useState(null);
  const audioTrackRef = useRef(null);
  const leaveRoom = () => {
    console.log("leaving room");
    livekitRoom?.disconnect();
    audioTrackRef?.current?.stop();
  };

  const setMuted = (mute) => {
    if (!audioTrackRef?.current) {
      return;
    }
    if (mute) {
      console.log("Livekit Audio muted");
      audioTrackRef.current.mute();
    } else {
      console.log("Livekit Audio unmuted");
      audioTrackRef.current.unmute();
    }
  };

  const joinRoom = async (companionSessionUuid) => {
    try {
      const newRoom = new Room();

      newRoom.on(RoomEvent.Connected, () => {
        console.log("Successfully connected to LiveKit room");
        setLivekitRoom(newRoom);
      });

      newRoom.on(RoomEvent.Disconnected, () => {
        console.log("Disconnected from LiveKit room");
        setLivekitRoom(null);
      });

      const { data } = await getCompanionLivekitToken({
        variables: {
          companionSessionUuid,
        },
      });
      const { livekitUrl, token } =
        data.getCompanionSessionLiveKitToken;
      await newRoom.connect(livekitUrl, token);

      const tracks = await createLocalTracks({ audio: true });
      const audioTrack = tracks.find(
        (t) => t instanceof LocalAudioTrack,
      );

      if (audioTrack) {
        await newRoom.localParticipant.publishTrack(audioTrack);
        audioTrackRef.current = audioTrack;
      }
    } catch (error) {
      console.error("Error connecting to LiveKit:", error);
      setLivekitRoom(null);
    }
  };

  const sendText = async (text) => {
    await livekitRoom.localParticipant.sendText(text, {
      topic: "lk.chat",
    });
  };

  return (
    <CompanionContext.Provider
      value={{
        livekitRoom,
        sendText,
        joinRoom,
        leaveRoom,
        companionSession,
        setCompanionSession,
        setMuted,
      }}
    >
      {children}
    </CompanionContext.Provider>
  );
}

export function useCompanion() {
  return useContext(CompanionContext);
}
